import { useState } from 'react'
import { Alert, Button, Form, Input, Modal, Select, DatePicker, Tag, Popconfirm, InputNumber, Drawer, Descriptions, App, Space } from 'antd'
import { CheckCircleOutlined, DollarOutlined, PlusOutlined, WarningOutlined } from '@ant-design/icons'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { purchaseInvoicesApi } from '@/features/procurement/api'
import { plantsApi } from '@/features/masters/plants/api'
import { suppliersApi } from '@/features/gate/api'
import type { PurchaseInvoice } from '@/shared/types/procurement'
import DataTable from '@/shared/components/DataTable'
import FormDrawer from '@/shared/components/FormDrawer'
import PageHeader from '@/shared/components/PageHeader'
import { usePermission } from '@/shared/hooks/usePermission'

const STATUS_COLORS: Record<string, string> = {
  PENDING: 'orange', '3WAY_MATCHED': 'purple', APPROVED: 'green',
  PAID: 'cyan', DISPUTED: 'red',
}

export default function PurchaseInvoicePage() {
  const { message } = App.useApp()
  const qc = useQueryClient()
  const [form] = Form.useForm()
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [detailInv, setDetailInv] = useState<PurchaseInvoice | null>(null)
  const [plantFilter, setPlantFilter] = useState<string | undefined>()
  const [statusFilter, setStatusFilter] = useState<string | undefined>()

  const canCreate = usePermission('purchase_invoice.create')
  const canApprove = usePermission('purchase_invoice.approve')

  const { data, isLoading } = useQuery({
    queryKey: ['invoices', plantFilter, statusFilter],
    queryFn: () => purchaseInvoicesApi.list({ plant_id: plantFilter, status: statusFilter }),
  })
  const { data: plantsData } = useQuery({ queryKey: ['plants-all'], queryFn: () => plantsApi.list({ page: 1, page_size: 500 }) })
  const { data: suppliers } = useQuery({ queryKey: ['suppliers-all'], queryFn: () => suppliersApi.list({ limit: 500 }) })

  const plantOptions = plantsData?.items.map(p => ({ label: p.name, value: p.id })) ?? []
  const supplierOptions = suppliers?.items?.map((s: any) => ({ label: s.supplier_name, value: s.id })) ?? []

  const createMutation = useMutation({
    mutationFn: (values: Record<string, unknown>) => {
      const payload = {
        ...values,
        inv_date: dayjs(values.inv_date as string).format('YYYY-MM-DD'),
      }
      return purchaseInvoicesApi.create(payload)
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['invoices'] }); message.success('Invoice created'); setDrawerOpen(false) },
    onError: (e: any) => message.error(e.response?.data?.detail || 'Create failed'),
  })

  const matchMutation = useMutation({
    mutationFn: (id: string) => purchaseInvoicesApi.match(id),
    onSuccess: (result: any) => {
      qc.invalidateQueries({ queryKey: ['invoices'] })
      const status = result?.status ?? result?.invoice?.status
      if (status === '3WAY_MATCHED') {
        message.success('3-way match successful — invoice ready for approval')
      } else {
        // Show detailed DISPUTED reason
        Modal.warning({
          title: 'Match Failed — Invoice marked DISPUTED',
          content: (
            <div>
              <p>{result?.reason}</p>
              {result?.open_rejections?.length > 0 && (
                <Alert
                  type="error"
                  showIcon
                  icon={<WarningOutlined />}
                  message={`Open Rejection Notes: ${result.open_rejections.join(', ')}`}
                />
              )}
            </div>
          ),
        })
      }
    },
    onError: (e: any) => message.error(e?.response?.data?.detail || 'Match failed'),
  })

  const paidMutation = useMutation({
    mutationFn: ({ id, ref }: { id: string; ref?: string }) =>
      purchaseInvoicesApi.markPaid(id, ref),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['invoices'] }); message.success('Invoice marked as PAID') },
    onError: (e: any) => message.error(e?.response?.data?.detail || 'Failed to mark paid'),
  })

  const approveMutation = useMutation({
    mutationFn: (id: string) => purchaseInvoicesApi.approve(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['invoices'] }); message.success('Invoice approved') },
    onError: () => message.error('Approval failed'),
  })

  const columns = [
    { title: 'Invoice No', dataIndex: 'inv_no', key: 'inv_no', width: 140 },
    { title: 'Date', dataIndex: 'inv_date', key: 'inv_date', width: 100 },
    { title: 'Supplier', dataIndex: 'supplier_name', key: 'supplier_name' },
    { title: 'Taxable', dataIndex: 'taxable_value', key: 'taxable_value', width: 110,
      render: (v: string) => `₹${Number(v).toLocaleString('en-IN')}` },
    { title: 'Net Payable', dataIndex: 'net_payable', key: 'net_payable', width: 120,
      render: (v: string) => <strong>₹{Number(v).toLocaleString('en-IN')}</strong> },
    {
      title: 'Status', dataIndex: 'status', key: 'status', width: 130,
      render: (v: string) => <Tag color={STATUS_COLORS[v]}>{v}</Tag>,
    },
    {
      title: 'Actions', key: 'actions', width: 200,
      render: (_: unknown, r: PurchaseInvoice) => (
        <div style={{ display: 'flex', gap: 4 }}>
          <a onClick={() => setDetailInv(r)}>View</a>
          {r.status === 'PENDING' && (
            <Popconfirm title="Run 3-way match?" onConfirm={() => matchMutation.mutate(r.id)}>
              <Button size="small">Match</Button>
            </Popconfirm>
          )}
          {r.status === '3WAY_MATCHED' && canApprove && (
            <Popconfirm title="Approve invoice for payment?" onConfirm={() => approveMutation.mutate(r.id)}>
              <Button size="small" type="primary" icon={<CheckCircleOutlined />}>Approve</Button>
            </Popconfirm>
          )}
          {r.status === 'APPROVED' && (
            <Popconfirm title="Mark this invoice as PAID?" onConfirm={() => paidMutation.mutate({ id: r.id })}>
              <Button size="small" icon={<DollarOutlined />} style={{ color: '#52c41a', borderColor: '#52c41a' }}>
                Mark Paid
              </Button>
            </Popconfirm>
          )}
        </div>
      ),
    },
  ]

  return (
    <div>
      <PageHeader
        title="Purchase Invoices"
        subtitle="Record supplier invoices and perform 3-way matching"
        extra={canCreate && <Button type="primary" icon={<PlusOutlined />} onClick={() => { form.resetFields(); form.setFieldsValue({ cgst: 0, sgst: 0, igst: 0, tds_deducted: 0 }); setDrawerOpen(true) }}>New Invoice</Button>}
      />

      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <Select allowClear placeholder="Plant" options={plantOptions} onChange={v => setPlantFilter(v)} style={{ width: 180 }} />
        <Select allowClear placeholder="Status"
          options={['PENDING', '3WAY_MATCHED', 'APPROVED', 'PAID', 'DISPUTED'].map(s => ({ label: s, value: s }))}
          onChange={v => setStatusFilter(v)} style={{ width: 150 }}
        />
      </div>

      <DataTable rowKey="id" columns={columns} dataSource={data} loading={isLoading} />

      <FormDrawer
        title="New Purchase Invoice"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onSubmit={() => form.validateFields().then(v => createMutation.mutate(v))}
        loading={createMutation.isPending}
        form={form}
        width={600}
      >
        <Form.Item name="inv_no" label="Invoice No" rules={[{ required: true }]}><Input /></Form.Item>
        <Form.Item name="inv_date" label="Invoice Date" rules={[{ required: true }]}>
          <DatePicker style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name="plant_id" label="Plant" rules={[{ required: true }]}>
          <Select options={plantOptions} />
        </Form.Item>
        <Form.Item name="supplier_id" label="Supplier" rules={[{ required: true }]}>
          <Select options={supplierOptions} showSearch optionFilterProp="label" />
        </Form.Item>
        <Form.Item name="taxable_value" label="Taxable Value (₹)" rules={[{ required: true }]}>
          <InputNumber style={{ width: '100%' }} min={0} />
        </Form.Item>
        <div style={{ display: 'flex', gap: 8 }}>
          <Form.Item name="cgst" label="CGST" style={{ flex: 1 }}>
            <InputNumber style={{ width: '100%' }} min={0} />
          </Form.Item>
          <Form.Item name="sgst" label="SGST" style={{ flex: 1 }}>
            <InputNumber style={{ width: '100%' }} min={0} />
          </Form.Item>
          <Form.Item name="igst" label="IGST" style={{ flex: 1 }}>
            <InputNumber style={{ width: '100%' }} min={0} />
          </Form.Item>
        </div>
        <Form.Item name="tds_deducted" label="TDS Deducted">
          <InputNumber style={{ width: '100%' }} min={0} />
        </Form.Item>
        <Form.Item name="net_payable" label="Net Payable (₹)" rules={[{ required: true }]}>
          <InputNumber style={{ width: '100%' }} min={0} />
        </Form.Item>
        <Form.Item name="po_id" label="PO ID (for 3-way match)">
          <Input placeholder="Optional — UUID" />
        </Form.Item>
        <Form.Item name="wo_id" label="WO ID (for service match)">
          <Input placeholder="Optional — UUID" />
        </Form.Item>
        <Form.Item name="remarks" label="Remarks"><Input.TextArea rows={2} /></Form.Item>
      </FormDrawer>

      <Drawer title="Invoice Details" open={!!detailInv} onClose={() => setDetailInv(null)} width={460}>
        {detailInv && (
          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label="Invoice No">{detailInv.inv_no}</Descriptions.Item>
            <Descriptions.Item label="Date">{detailInv.inv_date}</Descriptions.Item>
            <Descriptions.Item label="Supplier">{detailInv.supplier_name}</Descriptions.Item>
            <Descriptions.Item label="PO No">{detailInv.po_no || '—'}</Descriptions.Item>
            <Descriptions.Item label="WO No">{detailInv.wo_no || '—'}</Descriptions.Item>
            <Descriptions.Item label="Taxable Value">₹{Number(detailInv.taxable_value).toLocaleString('en-IN')}</Descriptions.Item>
            <Descriptions.Item label="CGST">₹{Number(detailInv.cgst).toLocaleString('en-IN')}</Descriptions.Item>
            <Descriptions.Item label="SGST">₹{Number(detailInv.sgst).toLocaleString('en-IN')}</Descriptions.Item>
            <Descriptions.Item label="IGST">₹{Number(detailInv.igst).toLocaleString('en-IN')}</Descriptions.Item>
            <Descriptions.Item label="TDS Deducted">₹{Number(detailInv.tds_deducted).toLocaleString('en-IN')}</Descriptions.Item>
            <Descriptions.Item label="Net Payable"><strong>₹{Number(detailInv.net_payable).toLocaleString('en-IN')}</strong></Descriptions.Item>
            <Descriptions.Item label="Status"><Tag color={STATUS_COLORS[detailInv.status]}>{detailInv.status}</Tag></Descriptions.Item>
          </Descriptions>
        )}
      </Drawer>
    </div>
  )
}
